import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControlLabel, Radio } from '@mui/material';
import {
    AddCircleBlue,
    CustomDelete,
    CustomizedSwitch,
    EditWithIcon,
    ManageActions,
    NoItemsYet,
    StatusCard,
    ToolInfo,
    SimpleTooltip,
    NotError,
    MinLoader,
} from 'components';
import {
    FindLoad,
    useModal,
    renderGoalValueType,
    toCamelCase,
    goalDataTypeEnums,
    Images,
    masteryDirectionEnums,
    masteryDirectionList,
    masteryEvalTypeEnums,
    masteryEvalTypeList,
    renderTargetStatuses,
    targetStatusEnums,
    Colors,
    PermissionList,
    RolePermission,
} from 'utils';
import { goalActions, libraryActions } from 'store';
import { toast } from 'react-toastify';
import {
    FirstTrialResponseSelect,
    FirstTrialStepCondition,
    FirstTrialSwitch,
    isFirstTrialActive,
    isTotalEvalType,
    toggleFirstTrial,
    validateFirstTrialStep,
    withFirstTrialStepDefaults,
} from './firstTrialCriteria';
import { CriteriaSaveBar, DurationCalcSelect, isDurationGoal, MAINTENANCE_FIELDS, pickMaintenanceSettings, useCriteriaDraft } from './criteriaForm';

const sessionEnums = {
    BY_SESSION: 'BY_SESSION',
    BY_DAY: 'BY_DAY',
};

const maintenancesInfo = {
    showTargetEvery: 1,
    moveNextIfValue: 0,
    moveNextAcross: 1,
    moveNextIfDirection: masteryDirectionEnums.ABOVE,
    moveBackIfValue: 0,
    moveBackAcross: 1,
    moveBackIfDirection: masteryDirectionEnums.BELOW,
};

const ADD_STEP_ACTION_TYPE = 'ADD_TARGET_MAINTENANCE_STEP';
const EDIT_STEP_ACTION_TYPE = 'EDIT_TARGET_MAINTENANCE_STEP';
const DELETE_ACTION_TYPE = 'DELETE_TARGET_MAINTENANCE_STEP';
const MAINTENANCE_AUTO_MASTERY = 'CHANGE_TARGET_MAINTENANCE';
export const TargetMaintenance = ({ isLibrary }) => {
    const goalById = useSelector((state) => (isLibrary ? state.library.goalById : state.goals.goalById));
    const dispatch = useDispatch();
    const actions = isLibrary ? libraryActions : goalActions;
    const actionTypes = isLibrary
        ? {
              ADD_STEP: 'ADD_LIBRARY_TARGET_MAINTENANCE_STEP',
              EDIT_STEP: 'EDIT_LIBRARY_TARGET_MAINTENANCE_STEP',
              DELETE_STEP: 'DELETE_LIBRARY_TARGET_MAINTENANCE_STEP',
              CHANGE: 'CHANGE_LIBRARY_TARGET_MAINTENANCE',
          }
        : {
              ADD_STEP: ADD_STEP_ACTION_TYPE,
              EDIT_STEP: EDIT_STEP_ACTION_TYPE,
              DELETE_STEP: DELETE_ACTION_TYPE,
              CHANGE: MAINTENANCE_AUTO_MASTERY,
          };
    const [inputs, setInputs] = useState({});
    const [actionKey, setActionKey] = useState('');
    const [error, setError] = useState('');
    const addStepLoader = FindLoad(actionTypes.ADD_STEP);
    const loadMaintenance = FindLoad(actionTypes.CHANGE);
    const { open } = useModal();
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);
    const firstTrialActive = isFirstTrialActive(goalById, inputs);
    // Maintenance settings are edited as a draft and saved with the Save bar; steps keep their own ✓ save.
    const settings = useCriteriaDraft({ fields: MAINTENANCE_FIELDS, actionType: actionTypes.CHANGE });
    const settingsFirstTrial = isFirstTrialActive(goalById, settings.draft);

    useEffect(() => {
        if (goalById?.maintenance?.id) {
            const params = {
                ...goalById?.maintenance,
            };

            if (goalById?.maintenance?.steps?.length) {
                // Clone — every step onChange handler below mutates params.steps[j] in place;
                // assigning the Redux-owned array/objects directly here made those edits mutate
                // the store outside a reducer (RTK's immutableStateInvariantMiddleware caught
                // this as "A state mutation was detected between dispatches").
                params.steps = structuredClone(goalById?.maintenance?.steps);
            } else {
                if (goalById?.maintenance?.autoMaintenance) {
                    // Clone — maintenancesInfo is a module-level constant; without cloning it here,
                    // the same in-place mutation would have corrupted the shared default for every
                    // goal for the lifetime of the page.
                    params.steps = [structuredClone(maintenancesInfo)];
                }
            }
            setInputs(params);
            settings.sync(pickMaintenanceSettings(params));
            // setInputs({
            //     ...goalById?.maintenance,
            // });
        } else {
            const defaults = {
                // 'autoMaintenance': true,
                autoMaintenance: false,
                evalMode: sessionEnums.BY_SESSION,
                evalType: masteryEvalTypeEnums.CONSECUTIVE,
                steps: [],
            };
            setInputs(defaults);
            settings.sync(pickMaintenanceSettings(defaults));
        }
    }, [goalById]);

    const handleChangeEvalMode = (e) => settings.update({ evalMode: e.target?.value });

    const handleChangeEvalType = (e) => settings.update({ evalType: e.target?.value });

    const handleSave = (body) => {
        if (isLibrary) {
            dispatch(actions.changeLibraryTargetMaintenance(goalById?.id, body));
        } else {
            dispatch(actions.changeTargetMaintenance(goalById?.id, body));
        }
    };

    const validateStep = (item, key) => {
        if (!item) return { valid: true };
        if (firstTrialActive) return validateFirstTrialStep(item, inputs);

        const { moveNextIfDirection, moveBackIfDirection, moveNextIfValue, moveBackIfValue } = item;

        const isEqual = moveNextIfDirection === masteryDirectionEnums.EQUAL || moveBackIfDirection === masteryDirectionEnums.EQUAL;

        if (!isEqual && moveNextIfDirection === masteryDirectionEnums.ABOVE && moveBackIfDirection === masteryDirectionEnums.ABOVE) {
            toast(<NotError type="error" errMessage="Both Next If and Back If directions cannot be Above" />);
            return { valid: false, field: 'moveNextIfDirection' };
        }

        if (!isEqual && moveNextIfDirection === masteryDirectionEnums.BELOW && moveBackIfDirection === masteryDirectionEnums.BELOW) {
            toast(<NotError type="error" errMessage="Both Next If and Back If directions cannot be Below" />);
            return { valid: false, field: 'moveNextIfDirection' };
        }

        if (!isEqual && moveNextIfDirection === masteryDirectionEnums.ABOVE && moveNextIfValue <= moveBackIfValue) {
            toast(<NotError type="error" errMessage="Next If value must be greater than Back If value for Above direction" />);
            return { valid: false, field: 'moveNextIfValue' };
        }

        if (!isEqual && moveBackIfDirection === masteryDirectionEnums.ABOVE && moveBackIfValue <= moveNextIfValue) {
            toast(<NotError type="error" errMessage="Back If value must be greater than Next If value for Above direction" />);
            return { valid: false, field: 'moveBackIfValue' };
        }

        const required =
            item.showTargetEvery > 0 &&
            item.moveNextIfValue > 0 &&
            item.moveNextAcross > 0 &&
            item.moveBackIfValue > 0 &&
            item.moveBackAcross > 0;

        if (!required) {
            const errField = !item.showTargetEvery
                ? 'showTargetEvery'
                : !item.moveNextIfValue
                  ? 'moveNextIfValue'
                  : !item.moveNextAcross
                    ? 'moveNextAcross'
                    : !item.moveBackIfValue
                      ? 'moveBackIfValue'
                      : !item.moveBackAcross
                        ? 'moveBackAcross'
                        : '';

            return { valid: false, field: errField };
        }

        return { valid: true };
    };

    const handleAction = (item, actionType, key) => {
        if (actionType === 'save') {
            const { valid, field } = validateStep(item, key);

            if (!valid) {
                setError({ key, field });
                return;
            }

            setError('');
            setActionKey(key);

            const safeItem = structuredClone(firstTrialActive ? withFirstTrialStepDefaults(item) : item);
            const params = {};

            if (safeItem?.id) {
                open(
                    <CustomDelete
                        info="Update maintenance step?"
                        text="These changes will apply to all targets assigned to this step"
                        innerText="Update Step"
                        color="blue"
                        handleDel={() =>
                            dispatch(
                                isLibrary
                                    ? actions.editLibraryTargetMaintenanceStep(goalById?.id, safeItem?.id, safeItem)
                                    : actions.editTargetMaintenanceStep(goalById?.id, safeItem?.id, safeItem)
                            )
                        }
                        actionType={actionTypes.EDIT_STEP}
                        body={
                            <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                                <img style={{ marginTop: '3px' }} src={Images.infoCircle} alt="icon" />
                                <p>Updating this step will recalculate progress for all targets based on the new rules</p>
                            </div>
                        }
                    />
                );
            } else {
                dispatch(
                    isLibrary
                        ? actions.addLibraryTargetMaintenanceStep(goalById?.id, safeItem)
                        : actions.addTargetMaintenanceStep(goalById?.id, safeItem)
                );
            }
        }

        if (actionType === 'delete') {
            setError('');

            const text =
                inputs?.steps?.length === 1
                    ? 'If you delete this step, the system will automatically set all targets assigned to it to Mastered'
                    : 'Deleting this step will automatically move all targets assigned to it to the previous step';

            open(
                <CustomDelete
                    info="Delete maintenance step?"
                    text="This action will remove this step from the maintenance flow."
                    innerText="Delete Step"
                    handleDel={() =>
                        dispatch(
                            isLibrary
                                ? actions.deleteLibraryTargetMaintenanceStep(goalById?.id, item?.id)
                                : actions.deleteTargetMaintenanceStep(goalById?.id, item?.id)
                        )
                    }
                    actionType={actionTypes.DELETE_STEP}
                    body={
                        <div className="delete-step-info-wrapper">
                            <div className="delete-step-info-box">
                                <img src={Images.infoCircle} alt="info" />
                                <p>{text}</p>
                            </div>
                            <div className="delete-step-information">
                                <img src={Images.arrowRight} alt="arrow" />
                                <p>If you don’t want that, update those targets manually first</p>
                            </div>
                        </div>
                    }
                />
            );
        }
    };

    const handleAddStep = () => {
        const lastIndex = inputs?.steps?.length - 1;
        const lastItem = inputs?.steps[lastIndex];

        const { valid, field } = validateStep(lastItem, lastIndex);

        if (!valid) {
            setError({ key: lastIndex, field });
            return;
        }

        if (inputs?.steps?.length === 0) {
            setInputs((prev) => ({
                ...prev,
                steps: [...prev.steps, structuredClone(maintenancesInfo)],
            }));
        } else {
            if (lastItem?.id) {
                setInputs((prev) => ({
                    ...prev,
                    steps: [...prev.steps, structuredClone(maintenancesInfo)],
                }));
            }
        }
    };

    if (!goalById?.goalMastery?._id && !goalById?.targetMastery?._id) {
        return <NoItemsYet text={'No Maintenance Yet'} image={Images.emptyFolder} />;
    }

    return (
        <div>
            <div className="mastery-box">
                <p className="mastery-title">Target Maintenance</p>
                <div className="mastery-info-box">
                    <div className="mastery-status-row">
                        <div className="space-between-align-center" style={{ padding: 12 }}>
                            <div className="flex-align-center" style={{ gap: 4 }}>
                                <div className="flex-align-center" style={{ gap: 4 }}>
                                    <StatusCard
                                        status={renderTargetStatuses?.find((j) => targetStatusEnums.MASTERED === j?.value)?.label}
                                        custom={renderTargetStatuses?.find((j) => targetStatusEnums.MASTERED === j?.value)}
                                    />
                                    <img src={Images.arrowRight} alt="arrow" />
                                    <StatusCard
                                        status={renderTargetStatuses?.find((j) => targetStatusEnums.MAINTENANCE === j?.value)?.label}
                                        custom={renderTargetStatuses?.find((j) => targetStatusEnums.MAINTENANCE === j?.value)}
                                    />
                                    <img src={Images.arrowRight} alt="arrow" />
                                    <StatusCard
                                        status={renderTargetStatuses?.find((j) => targetStatusEnums.COMPLETED === j?.value)?.label}
                                        custom={renderTargetStatuses?.find((j) => targetStatusEnums.COMPLETED === j?.value)}
                                    />
                                </div>

                                <div className="target-maintenance-info">
                                    <img src={Images.infoCircle} alt="infoCircle" />
                                    <p>Target will move to “Complete” after this maintenance check</p>
                                </div>
                            </div>
                            <div className="flex-align-center" style={{ gap: 16 }}>
                                <FirstTrialSwitch
                                    goal={goalById}
                                    disabled={!hasPermission || !settings.draft?.autoMaintenance}
                                    checked={settings.draft?.firstTrialOnly}
                                    onToggle={() => settings.update(toggleFirstTrial(settings.draft))}
                                />
                                <div className="switcher-style" style={{ gap: 16 }}>
                                    <p style={{ width: 'auto', padding: 0 }}>Auto Maintenance</p>
                                    <div style={{ width: 30 }}>
                                        {loadMaintenance?.length ? (
                                            <MinLoader margin={'0'} color={Colors.ThemeBlue} position={'relative'} />
                                        ) : (
                                            <CustomizedSwitch
                                                disabled={!hasPermission}
                                                checked={settings.draft?.autoMaintenance}
                                                handleClick={() => settings.update({ autoMaintenance: !settings.draft?.autoMaintenance })}
                                            />
                                        )}
                                    </div>
                                </div>
                                <ToolInfo
                                    text={
                                        'Automatically schedule maintenance probing sessions after a target is mastered to monitor and ensure skill retention.'
                                    }
                                />
                            </div>
                        </div>

                        <div
                            className="flex-align-center"
                            style={{ borderTop: '1px solid #EBF2FD', padding: 12, gap: 12, flexWrap: 'wrap', rowGap: 10 }}
                        >
                            <div className="flex-align-center" style={{ gap: 4 }}>
                                <ToolInfo text={'Applies to all maintenance steps for this goal'} />
                                <p style={{ marginBottom: 2 }}>Schedule steps based on:</p>
                            </div>

                            <div className="flex-align-center">
                                <FormControlLabel
                                    disabled={!settings.draft?.autoMaintenance || !hasPermission}
                                    onChange={handleChangeEvalMode}
                                    name="evalMode"
                                    value={sessionEnums.BY_SESSION}
                                    label="Sessions"
                                    control={
                                        <Radio
                                            style={{
                                                color: settings.draft?.evalMode === sessionEnums.BY_SESSION ? '#347AF0' : '#D0D5DD',
                                                padding: '0 8px',
                                                opacity: !settings.draft?.autoMaintenance ? 0.5 : 1,
                                            }}
                                            checked={settings.draft?.evalMode === sessionEnums.BY_SESSION}
                                        />
                                    }
                                />
                                <div style={{ height: 18, width: 1, background: '#E4E7EC', margin: '0 12px 0 0' }} />
                                <FormControlLabel
                                    disabled={!settings.draft?.autoMaintenance || !hasPermission || settingsFirstTrial}
                                    onChange={handleChangeEvalMode}
                                    name="evalMode"
                                    value={sessionEnums.BY_DAY}
                                    label="Days"
                                    control={
                                        <Radio
                                            style={{
                                                color: settings.draft?.evalMode === sessionEnums.BY_DAY ? '#347AF0' : '#D0D5DD',
                                                padding: '0 8px',
                                                opacity: !settings.draft?.autoMaintenance ? 0.5 : 1,
                                            }}
                                            checked={settings.draft?.evalMode === sessionEnums.BY_DAY}
                                        />
                                    }
                                />
                            </div>

                            <div className="flex-align-center" style={{ gap: 4 }}>
                                <ToolInfo
                                    text={
                                        'Consecutive: every session or day in a step’s range must meet that step’s condition. ' +
                                        'Total: the average across the range must meet it.'
                                    }
                                />
                                <p style={{ marginBottom: 2 }}>Evaluate steps by:</p>
                            </div>

                            <div className="mastery-percent-input-box mastery-eval-type-box">
                                <select
                                    disabled={!settings.draft?.autoMaintenance || !hasPermission}
                                    name="evalType"
                                    value={settings.draft?.evalType || masteryEvalTypeEnums.CONSECUTIVE}
                                    onChange={handleChangeEvalType}
                                >
                                    {masteryEvalTypeList?.map((i, j) => (
                                        <option key={j} value={i?.value}>
                                            {i?.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {isDurationGoal(goalById) && (
                                <div className="flex-align-center" style={{ gap: 8 }}>
                                    <ToolInfo text={'Total: the sum of a session’s trial durations. Average: their mean.'} />
                                    <p style={{ marginBottom: 2 }}>Session duration:</p>
                                    <DurationCalcSelect
                                        disabled={!settings.draft?.autoMaintenance || !hasPermission}
                                        value={settings.draft?.durationCalc}
                                        onChange={(durationCalc) => settings.update({ durationCalc })}
                                    />
                                </div>
                            )}
                            {settingsFirstTrial && (
                                <div className="flex-align-center" style={{ gap: 8 }}>
                                    <p style={{ marginBottom: 2 }}>Count first response:</p>
                                    <FirstTrialResponseSelect
                                        disabled={!settings.draft?.autoMaintenance || !hasPermission}
                                        value={settings.draft?.firstTrialResponse}
                                        onChange={(value) => settings.update({ firstTrialResponse: value })}
                                    />
                                </div>
                            )}
                        </div>
                        <CriteriaSaveBar
                            dirty={settings.isDirty}
                            saving={settings.isSaving}
                            onCancel={settings.reset}
                            onSave={() => settings.save(handleSave)}
                        />

                        <div style={{ borderTop: '1px solid #EBF2FD', padding: 12, gap: 10 }}>
                            {inputs?.steps?.map((item, j) => (
                                <div className="flex-align-start" style={{ gap: 12, width: '100%' }}>
                                    <div className="step-maintenance-count-box">
                                        <div className="step-maintenance-count">
                                            <p>{j + 1}</p>
                                        </div>
                                        <div className="step-maintenance-count-line" />
                                    </div>

                                    <div
                                        key={j}
                                        style={error?.key === j ? { borderBottom: '1px solid #FE7070' } : {}}
                                        className={'steps-maintenance-box'}
                                    >
                                        <div className="flex-align-center">
                                            <div className="flex-align-center" style={{ gap: 10 }}>
                                                <p className="target-mastery-info">Show every </p>
                                                <div className="target-days-wrapper">
                                                    <div
                                                        className="mastery-percent-input-box"
                                                        style={{
                                                            width: 50,
                                                            padding: '7px 8px',
                                                            borderColor:
                                                                error?.key === j && error?.field === 'showTargetEvery'
                                                                    ? '#FE7070'
                                                                    : '#D0D5DD',
                                                        }}
                                                    >
                                                        <input
                                                            disabled={!inputs?.autoMaintenance || !hasPermission}
                                                            type="number"
                                                            name="showTargetEvery"
                                                            min={1}
                                                            step={1}
                                                            value={item?.showTargetEvery}
                                                            onChange={(e) => {
                                                                const inputValue = e.target.value;
                                                                const params = { ...inputs };
                                                                if (inputValue === '') {
                                                                    params.steps[j].showTargetEvery = '';
                                                                } else {
                                                                    let value = Number(inputValue);
                                                                    if (value <= 0) value = 1;
                                                                    params.steps[j].showTargetEvery = value;
                                                                }
                                                                if (error?.key === j && error?.field === 'showTargetEvery') {
                                                                    setError('');
                                                                }
                                                                setInputs(params);
                                                            }}
                                                        />
                                                    </div>
                                                    <p>
                                                        {inputs?.evalMode === sessionEnums?.BY_DAY
                                                            ? 'day(s)'
                                                            : inputs?.evalMode === sessionEnums?.BY_SESSION
                                                              ? 'session(s)'
                                                              : ''}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex-align-center" style={{ gap: 10 }}>
                                                <p className="target-mastery-info">→ Next if </p>
                                                {firstTrialActive ? (
                                                    <FirstTrialStepCondition
                                                        kind="next"
                                                        criteria={inputs}
                                                        step={item}
                                                        disabled={!inputs?.autoMaintenance || !hasPermission}
                                                        onCountChange={(key, value) => {
                                                            const params = { ...inputs };
                                                            params.steps[j][key] = value;
                                                            setInputs(params);
                                                        }}
                                                    />
                                                ) : (
                                                    <>
                                                        <ManageActions
                                                            disabled={!inputs?.autoMaintenance || !hasPermission}
                                                            btnClass="mastery-direction-box"
                                                            btnTitle={toCamelCase(item?.moveNextIfDirection)}
                                                            btnIcon={
                                                                item?.moveNextIfDirection === masteryDirectionEnums.ABOVE
                                                                    ? Images.arrowUpGray
                                                                    : item?.moveNextIfDirection === masteryDirectionEnums.BELOW
                                                                      ? Images.arrowDown
                                                                      : null
                                                            }
                                                            renderBody={(handleClose) => (
                                                                <div>
                                                                    {masteryDirectionList?.map(
                                                                        (i, k) =>
                                                                            i?.value !== item?.moveNextIfDirection && (
                                                                                <button
                                                                                    style={{
                                                                                        justifyContent: 'start',
                                                                                        background: 'transparent',
                                                                                        border: 'none',
                                                                                        padding: '8px 0',
                                                                                        width: '100%',
                                                                                    }}
                                                                                    key={k}
                                                                                    className="mastery-direction-box"
                                                                                    onClick={() => {
                                                                                        const params = { ...inputs };
                                                                                        params.steps[j].moveNextIfDirection = i?.value;
                                                                                        setInputs(params);
                                                                                        handleClose();
                                                                                    }}
                                                                                >
                                                                                    <p>{i?.label}</p>
                                                                                </button>
                                                                            )
                                                                    )}
                                                                </div>
                                                            )}
                                                        />

                                                        <div
                                                            className="mastery-percent-input-box"
                                                            style={{
                                                                width: 65,
                                                                padding: '7px 8px',
                                                                borderColor:
                                                                    error?.key === j && error?.field === 'moveNextIfValue' ? '#FE7070' : '#D0D5DD',
                                                            }}
                                                        >
                                                            <input
                                                                disabled={!inputs?.autoMaintenance || !hasPermission}
                                                                type="number"
                                                                name="moveNextIfValue"
                                                                value={item?.moveNextIfValue}
                                                                onChange={(e) => {
                                                                    let value = +e.target.value;
                                                                    if (goalById?.dataType === goalDataTypeEnums?.PERCENT) {
                                                                        if (value > 100) value = 100;
                                                                    }
                                                                    const params = { ...inputs };
                                                                    params.steps[j].moveNextIfValue = value ? value : '';
                                                                    if (error?.key === j && error?.field === 'moveNextIfValue') {
                                                                        setError('');
                                                                    }
                                                                    setInputs(params);
                                                                }}
                                                            />
                                                            <p>{renderGoalValueType(goalById)}</p>
                                                        </div>
                                                    </>
                                                )}
                                                <p className="target-mastery-info">{firstTrialActive && isTotalEvalType(inputs) ? 'in' : 'for'}</p>
                                                <div className="target-days-wrapper">
                                                    <div
                                                        className="mastery-percent-input-box"
                                                        style={{
                                                            width: 50,
                                                            padding: '7px 8px',
                                                            borderColor:
                                                                error?.key === j && error?.field === 'moveNextAcross'
                                                                    ? '#FE7070'
                                                                    : '#D0D5DD',
                                                        }}
                                                    >
                                                        <input
                                                            disabled={!inputs?.autoMaintenance || !hasPermission}
                                                            name="moveNextAcross"
                                                            type="number"
                                                            value={item?.moveNextAcross}
                                                            onChange={(e) => {
                                                                const inputValue = e.target.value;
                                                                const params = { ...inputs };
                                                                if (inputValue === '') {
                                                                    params.steps[j].moveNextAcross = '';
                                                                } else {
                                                                    let value = Number(inputValue);
                                                                    if (value <= 0) value = 1;
                                                                    params.steps[j].moveNextAcross = value;
                                                                }
                                                                if (error?.key === j && error?.field === 'moveNextAcross') {
                                                                    setError('');
                                                                }
                                                                setInputs(params);
                                                            }}
                                                        />
                                                    </div>
                                                    <p>
                                                        {inputs?.evalMode === sessionEnums?.BY_DAY
                                                            ? 'day(s)'
                                                            : inputs?.evalMode === sessionEnums?.BY_SESSION
                                                              ? 'session(s)'
                                                              : ''}{' '}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex-align-center" style={{ gap: 10 }}>
                                                <p className="target-mastery-info">→ Back if</p>
                                                {firstTrialActive ? (
                                                    <FirstTrialStepCondition
                                                        kind="back"
                                                        criteria={inputs}
                                                        step={item}
                                                        disabled={!inputs?.autoMaintenance || !hasPermission}
                                                        onCountChange={(key, value) => {
                                                            const params = { ...inputs };
                                                            params.steps[j][key] = value;
                                                            setInputs(params);
                                                        }}
                                                    />
                                                ) : (
                                                    <>
                                                        <ManageActions
                                                            disabled={!inputs?.autoMaintenance || !hasPermission}
                                                            btnClass="mastery-direction-box"
                                                            btnTitle={toCamelCase(item?.moveBackIfDirection)}
                                                            btnIcon={
                                                                item?.moveBackIfDirection === masteryDirectionEnums.ABOVE
                                                                    ? Images.arrowUpGray
                                                                    : item?.moveBackIfDirection === masteryDirectionEnums.BELOW
                                                                      ? Images.arrowDown
                                                                      : null
                                                            }
                                                            renderBody={(handleClose) => (
                                                                <div>
                                                                    {masteryDirectionList?.map(
                                                                        (i, k) =>
                                                                            i?.value !== item?.moveBackIfDirection && (
                                                                                <button
                                                                                    style={{
                                                                                        justifyContent: 'start',
                                                                                        background: 'transparent',
                                                                                        border: 'none',
                                                                                        padding: '8px 0',
                                                                                        width: '100%',
                                                                                    }}
                                                                                    key={k}
                                                                                    className="mastery-direction-box"
                                                                                    onClick={() => {
                                                                                        const params = { ...inputs };
                                                                                        params.steps[j].moveBackIfDirection = i?.value;
                                                                                        setInputs(params);
                                                                                        handleClose();
                                                                                    }}
                                                                                >
                                                                                    <p>{i?.label}</p>
                                                                                </button>
                                                                            )
                                                                    )}
                                                                </div>
                                                            )}
                                                        />

                                                        <div
                                                            className="mastery-percent-input-box"
                                                            style={{
                                                                width: 65,
                                                                padding: '7px 8px',
                                                                borderColor:
                                                                    error?.key === j && error?.field === 'moveBackIfValue' ? '#FE7070' : '#D0D5DD',
                                                            }}
                                                        >
                                                            <input
                                                                disabled={!inputs?.autoMaintenance || !hasPermission}
                                                                type="number"
                                                                name="moveBackIfValue"
                                                                value={item?.moveBackIfValue}
                                                                onChange={(e) => {
                                                                    let value = +e.target.value;
                                                                    if (goalById?.dataType === goalDataTypeEnums?.PERCENT) {
                                                                        if (value > 100) value = 100;
                                                                    }
                                                                    const params = { ...inputs };
                                                                    params.steps[j].moveBackIfValue = value ? value : '';
                                                                    if (error?.key === j && error?.field === 'moveBackIfValue') {
                                                                        setError('');
                                                                    }
                                                                    setInputs(params);
                                                                }}
                                                            />
                                                            <p>{renderGoalValueType(goalById)}</p>
                                                        </div>
                                                    </>
                                                )}
                                                <p className="target-mastery-info">{firstTrialActive && isTotalEvalType(inputs) ? 'in' : 'for'}</p>
                                                <div className="flex-align-center" style={{ gap: 10 }}>
                                                    <div
                                                        className="mastery-percent-input-box"
                                                        style={{
                                                            width: 50,
                                                            padding: '7px 8px',
                                                            borderColor:
                                                                error?.key === j && error?.field === 'moveBackAcross'
                                                                    ? '#FE7070'
                                                                    : '#D0D5DD',
                                                        }}
                                                    >
                                                        <input
                                                            disabled={!inputs?.autoMaintenance || !hasPermission}
                                                            name="moveBackAcross"
                                                            type="number"
                                                            value={item?.moveBackAcross}
                                                            onChange={(e) => {
                                                                const inputValue = e.target.value;
                                                                const params = { ...inputs };
                                                                if (inputValue === '') {
                                                                    params.steps[j].moveBackAcross = '';
                                                                } else {
                                                                    let value = Number(inputValue);
                                                                    if (value <= 0) value = 1;
                                                                    params.steps[j].moveBackAcross = value;
                                                                }
                                                                if (error?.key === j && error?.field === 'moveBackAcross') {
                                                                    setError('');
                                                                }
                                                                setInputs(params);
                                                            }}
                                                        />
                                                    </div>
                                                    <p>
                                                        {inputs?.evalMode === sessionEnums?.BY_DAY
                                                            ? 'day(s)'
                                                            : inputs?.evalMode === sessionEnums?.BY_SESSION
                                                              ? 'session(s)'
                                                              : ''}{' '}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {inputs?.autoMaintenance && hasPermission && (
                                            <div
                                                className="flex-align-center"
                                                style={{
                                                    gap: 8,
                                                    borderLeft: '1px solid #E4E7EC',
                                                    marginLeft: 8,
                                                    paddingLeft: 12,
                                                }}
                                            >
                                                <EditWithIcon
                                                    load={addStepLoader?.length && actionKey === j}
                                                    id={'secondary-billing'}
                                                    icon={Images.checkmarkBlue}
                                                    type={'button'}
                                                    handleClick={() => {
                                                        handleAction(item, 'save', j);
                                                    }}
                                                />
                                                {!item?.id && (
                                                    <EditWithIcon
                                                        id={'secondary-billing'}
                                                        icon={Images.remove}
                                                        type={'button'}
                                                        handleClick={() => {
                                                            const params = { ...inputs };
                                                            params.steps.splice(j, 1);
                                                            setInputs(params);
                                                            if (error?.key === j) {
                                                                setError('');
                                                            }
                                                        }}
                                                    />
                                                )}
                                                {item?.id && (
                                                    <EditWithIcon
                                                        id={'secondary-billing'}
                                                        icon={Images.redTrash}
                                                        type={'button'}
                                                        handleClick={() => {
                                                            handleAction(item, 'delete', j);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            <div className="flex-align-center" style={{ gap: 16, padding: '6px 0' }}>
                                <StatusCard
                                    status={renderTargetStatuses?.find((j) => targetStatusEnums.COMPLETED === j?.value)?.label}
                                    custom={renderTargetStatuses?.find((j) => targetStatusEnums.COMPLETED === j?.value)}
                                />
                                {/*<button className="complete-btn-box">*/}
                                {/*    <img src={Images.completeStar} alt="complete" />*/}
                                {/*    <p>Complete</p>*/}
                                {/*</button>*/}
                                <p>OR</p>

                                {inputs?.autoMaintenance && hasPermission ? (
                                    <AddCircleBlue text={'Add Step'} icon={Images.addBluePlus} handleClick={handleAddStep} />
                                ) : (
                                    <div>
                                        <SimpleTooltip title={'Enable Auto Maintenance to add steps'} placement="top-start" arrow>
                                            <AddCircleBlue
                                                disabled={!inputs?.autoMaintenance}
                                                styles={{ opacity: !inputs?.autoMaintenance ? 0.5 : 1 }}
                                                text={'Add Step'}
                                                icon={Images.addBluePlus}
                                            />
                                        </SimpleTooltip>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
